import { z } from 'zod/v4'

export const WORKFLOW_LIST_ACTIVITY_DAYS = 30
export const WORKFLOW_LIST_HEALTH_RUN_LIMIT = 50

export const WorkflowListActivityPointSchema = z.object({
  x: z.string(),
  y: z.number(),
  label: z.string().optional(),
})

export type WorkflowListActivityPoint = z.infer<
  typeof WorkflowListActivityPointSchema
>

export const WorkflowListHealthSchema = z.object({
  successRate: z.number().nullable(),
  completedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
})

export type WorkflowListHealth = z.infer<typeof WorkflowListHealthSchema>

export const WorkflowListStatsSchema = z.object({
  activity: z.array(WorkflowListActivityPointSchema),
  health: WorkflowListHealthSchema,
})

export type WorkflowListStats = z.infer<typeof WorkflowListStatsSchema>

export type WorkflowRunListStatRow = {
  workflowId: string
  createdAt: Date | string
  completedAt?: Date | string | null
  failedAt?: Date | string | null
  cancelledAt?: Date | string | null
}

const toDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value)

const utcDayKey = (date: Date): string => date.toISOString().slice(0, 10)

const formatDayLabel = (dayKey: string): string => {
  const date = new Date(`${dayKey}T00:00:00.000Z`)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export const isWorkflowRunSuccessful = (
  run: Pick<
    WorkflowRunListStatRow,
    'completedAt' | 'failedAt' | 'cancelledAt'
  >,
): boolean => {
  if (run.failedAt) return false
  if (run.cancelledAt) return false
  return Boolean(run.completedAt)
}

export const buildEmptyWorkflowListStats = (
  now: Date = new Date(),
): WorkflowListStats => ({
  activity: buildActivityPoints([], now),
  health: {
    successRate: null,
    completedCount: 0,
    totalCount: 0,
  },
})

export const buildActivityPoints = (
  runs: Array<Pick<WorkflowRunListStatRow, 'createdAt'>>,
  now: Date = new Date(),
  dayCount: number = WORKFLOW_LIST_ACTIVITY_DAYS,
): WorkflowListActivityPoint[] => {
  const counts = new Map<string, number>()
  for (const run of runs) {
    const key = utcDayKey(toDate(run.createdAt))
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  const end = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  )
  const points: WorkflowListActivityPoint[] = []
  for (let offset = dayCount - 1; offset >= 0; offset--) {
    const day = new Date(end)
    day.setUTCDate(end.getUTCDate() - offset)
    const key = utcDayKey(day)
    points.push({
      x: key,
      y: counts.get(key) ?? 0,
      label: formatDayLabel(key),
    })
  }
  return points
}

export const buildHealthFromRuns = (
  runsNewestFirst: Array<
    Pick<
      WorkflowRunListStatRow,
      'completedAt' | 'failedAt' | 'cancelledAt'
    >
  >,
  limit: number = WORKFLOW_LIST_HEALTH_RUN_LIMIT,
): WorkflowListHealth => {
  const sample = runsNewestFirst.slice(0, limit)
  const totalCount = sample.length
  if (totalCount === 0) {
    return {
      successRate: null,
      completedCount: 0,
      totalCount: 0,
    }
  }

  const completedCount = sample.filter(isWorkflowRunSuccessful).length
  return {
    successRate: Math.round((100 * completedCount) / totalCount),
    completedCount,
    totalCount,
  }
}

/**
 * Build per-workflow list stats from run rows.
 * `healthRunsNewestFirst` should already be newest-first; only the first 50 per
 * workflow are used for health. Pass `activityRuns` for an accurate 30-day window
 * (all runs in range); otherwise health rows are filtered by day window.
 */
export const buildWorkflowListStatsByWorkflowId = (
  healthRunsNewestFirst: WorkflowRunListStatRow[],
  workflowIds: string[],
  now: Date = new Date(),
  options?: {
    activityDays?: number
    healthLimit?: number
    activityRuns?: Array<Pick<WorkflowRunListStatRow, 'workflowId' | 'createdAt'>>
  },
): Record<string, WorkflowListStats> => {
  const activityDays = options?.activityDays ?? WORKFLOW_LIST_ACTIVITY_DAYS
  const healthLimit = options?.healthLimit ?? WORKFLOW_LIST_HEALTH_RUN_LIMIT

  const activityStart = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  )
  activityStart.setUTCDate(activityStart.getUTCDate() - (activityDays - 1))

  const healthByWorkflow = new Map<string, WorkflowRunListStatRow[]>()
  const activityByWorkflow = new Map<
    string,
    Array<Pick<WorkflowRunListStatRow, 'createdAt'>>
  >()
  for (const id of workflowIds) {
    healthByWorkflow.set(id, [])
    activityByWorkflow.set(id, [])
  }

  for (const run of healthRunsNewestFirst) {
    const bucket = healthByWorkflow.get(run.workflowId)
    if (!bucket) continue
    bucket.push(run)
  }

  const activitySource =
    options?.activityRuns ??
    healthRunsNewestFirst.filter(
      (run) => toDate(run.createdAt).getTime() >= activityStart.getTime(),
    )
  for (const run of activitySource) {
    const bucket = activityByWorkflow.get(run.workflowId)
    if (!bucket) continue
    bucket.push(run)
  }

  const result: Record<string, WorkflowListStats> = {}
  for (const workflowId of workflowIds) {
    result[workflowId] = {
      activity: buildActivityPoints(
        activityByWorkflow.get(workflowId) ?? [],
        now,
        activityDays,
      ),
      health: buildHealthFromRuns(
        healthByWorkflow.get(workflowId) ?? [],
        healthLimit,
      ),
    }
  }
  return result
}
